/* eslint-disable quotes */
/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import {
  Table,
  Spin,
  Button,
  Modal,
  Form,
  Input,
  message,
  Tag,
  Switch,
  Select,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import "./areaList.scss";
import urls from "../../../api/urls";
import axios from "axios";

const { AREA_MANAGEMENT_LIST, AREA_MANAGEMENT_CREATE, AREA_MANAGEMENT_UPDATE } =
  urls;

const AreaList = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));

  // Create form
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  // View modal
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Filter
  const [statusFilter, setStatusFilter] = useState(undefined);

  useEffect(() => {
    getTableData();
  }, [pageSize, pageNum, statusFilter]);

  const getToken = () => {
    return userinfo?.accessToken;
  };

  const getHeaders = () => {
    const token = getToken();
    return {
      kid: "1",
      authtype: "jwt",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const getTableData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        message.error("No user token available");
        return;
      }

      const response = await axios.get(`${AREA_MANAGEMENT_LIST}?spId=${userinfo?.spId}&cpId=${userinfo?.cpId}`, {
        headers: getHeaders(),
      });

      if (response?.data) {
        const resData = response.data;
        const areas = Array.isArray(resData.data)
          ? resData.data
          : Array.isArray(resData)
            ? resData
            : [];

        const mappedAreas = areas.map((item) => ({
          ...item,
          areaName: item.name,
        }));

        // Client-side status filter
        let filteredAreas = mappedAreas;
        if (statusFilter !== undefined) {
          filteredAreas = mappedAreas.filter(
            (item) => item.isActive === statusFilter
          );
        }

        setTotal(filteredAreas.length);
        setTableData(filteredAreas);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to fetch area list"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);

      const token = getToken();
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        setCreating(false);
        return;
      }

      const payload = {
        areaName: values.areaName,
        cpId: userinfo?.cpId,
        spId: userinfo?.spId,
        roId: userinfo?.roId,
        userId: userinfo?.userId,
        isActive: true,
        createdBy: userinfo?.userId,
      };

      const response = await axios.post(AREA_MANAGEMENT_CREATE, payload, {
        headers: getHeaders(),
      });

      if (response?.data) {
        message.success(
          response?.data?.message || "Area created successfully"
        );
        createForm.resetFields();
        getTableData();
      }
    } catch (error) {
      if (error?.errorFields) return; // form validation error
      message.error(
        error?.response?.data?.message || "Failed to create area"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedArea(record);
    editForm.setFieldsValue({
      areaName: record.areaName,
      isActive: record.isActive,
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      setEditing(true);

      const token = getToken();
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        setEditing(false);
        return;
      }

      const payload = {
        areaId: selectedArea?.areaId,
        areaName: values.areaName,
        isActive: values.isActive,
        updatedBy: userinfo?.userId || 3,
      };

      const response = await axios.patch(AREA_MANAGEMENT_UPDATE, payload, {
        headers: getHeaders(),
      });

      if (response?.data) {
        message.success(
          response?.data?.message || "Area updated successfully"
        );
        setIsEditModalVisible(false);
        editForm.resetFields();
        setSelectedArea(null);
        getTableData();
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(
        error?.response?.data?.message || "Failed to update area"
      );
    } finally {
      setEditing(false);
    }
  };

  const handleView = (record) => {
    setViewData(record);
    setIsViewModalVisible(true);
  };

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return (
      <div>
        <p>{originalElement}</p>
      </div>
    );
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      align: "left",
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1,
    },
    {
      title: "Area Name",
      dataIndex: "areaName",
      key: "areaName",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Area Name"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.areaName?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 150,
      align: "left",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="- SELECT -"
            allowClear
            style={{ width: 150 }}
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              setPageNum(1);
            }}
          >
            <Select.Option value={true}>Active</Select.Option>
            <Select.Option value={false}>Inactive</Select.Option>
          </Select>
        </div>
      ),
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 130,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="View">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handlePageChange = (pagination) => {
    setPageSize(pagination.pageSize);
    setPageNum(pagination.current);
  };

  return (
    <Spin spinning={loading}>
      <div className="master-main-container m-1 p-1">
        {/* Create Area Section */}
        <div className="create-area-section">
          <div className="section-header">
            <h3>Create Area</h3>
          </div>
          <Form form={createForm} layout="vertical">
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <Form.Item
                label="Area Name"
                name="areaName"
                style={{ flex: 1, maxWidth: 500, marginBottom: 0 }}
                rules={[
                  {
                    required: true,
                    message: "Please enter area name",
                  },
                  {
                    min: 2,
                    message: "Area name must be at least 2 characters",
                  },
                ]}
              >
                <Input placeholder="Enter area name" />
              </Form.Item>
              <Button
                type="primary"
                loading={creating}
                onClick={handleCreate}
                style={{ marginTop: 30 }}
              >
                Create
              </Button>
            </div>
          </Form>
        </div>

        {/* Manage Areas Section */}
        <div className="manage-area-section">
          <div className="section-header">
            <h3>Manage Areas</h3>
            {total > 0 && (
              <span className="showing-text">
                Showing {(pageNum - 1) * pageSize + 1}-
                {Math.min(pageNum * pageSize, total)} of {total} items.
              </span>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: pageSize,
              itemRender: itemRender,
              total: total,
              current: pageNum,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            onChange={handlePageChange}
            rowKey={(record) =>
              record.areaId || record._id || Math.random().toString()
            }
            scroll={{ x: true }}
          />
        </div>

        {/* Edit Area Modal */}
        <Modal
          title="Update Area"
          open={isEditModalVisible}
          onCancel={() => {
            setIsEditModalVisible(false);
            editForm.resetFields();
            setSelectedArea(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setIsEditModalVisible(false);
                editForm.resetFields();
                setSelectedArea(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="update"
              type="primary"
              loading={editing}
              onClick={handleUpdate}
            >
              Update
            </Button>,
          ]}
          width={500}
        >
          <Form form={editForm} layout="vertical">
            <Form.Item
              label="Area Name"
              name="areaName"
              rules={[
                {
                  required: true,
                  message: "Please enter area name",
                },
              ]}
            >
              <Input placeholder="Enter area name" />
            </Form.Item>
            <Form.Item
              label="Status"
              name="isActive"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* View Area Modal */}
        <Modal
          title={viewData?.areaName || "Area Details"}
          open={isViewModalVisible}
          onCancel={() => {
            setIsViewModalVisible(false);
            setViewData(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setIsViewModalVisible(false);
                setViewData(null);
              }}
            >
              Close
            </Button>,
          ]}
          width={600}
        >
          {viewData && (
            <div className="area-detail-view">
              <table className="detail-table">
                <tbody>
                  <tr>
                    <td className="label-cell">HUB Name</td>
                    <td className="value-cell">
                      {viewData.hubName || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Channel Partner</td>
                    <td className="value-cell">
                      {viewData.channelPartner || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">User</td>
                    <td className="value-cell">
                      {viewData.userName || viewData.user || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Area Name</td>
                    <td className="value-cell">
                      {viewData.areaName || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Created Date Time</td>
                    <td className="value-cell">
                      {viewData.createdDate
                        ? new Date(viewData.createdDate).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label-cell">Status</td>
                    <td className="value-cell">
                      <Tag color={viewData.isActive ? "green" : "red"}>
                        {viewData.isActive ? "Active" : "Inactive"}
                      </Tag>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      </div>
    </Spin>
  );
};

export default AreaList;
